import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { storage } from '@infrastructure/firebase/FirebaseConfig';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

const STEP_UP_VALID_MS = 5 * 60 * 1000; // 5 minutes

export interface SecurityContextType {
  stepUpUnlockedAt: number | null;
  setStepUpUnlockedAt: (timestamp: number | null) => void;
  isStepUpValid: () => boolean;
  captureImplicitSelfie: (userId: string, actionType: string) => Promise<string | null>;
  webcamRef: React.RefObject<any>;
  isCapturing: boolean;
  STEP_UP_VALID_MS: number;
}

const SecurityContext = createContext<SecurityContextType | null>(null);

export function useSecurity(): SecurityContextType {
  const ctx = useContext(SecurityContext);
  if (!ctx) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return ctx;
}

interface SecurityProviderProps {
  children: ReactNode;
}

export function SecurityProvider({ children }: SecurityProviderProps) {
  const [stepUpUnlockedAt, setStepUpUnlockedAtState] = useState<number | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const webcamRef = useRef<any>(null);

  const captureImplicitSelfie = useCallback(async (userId: string, actionType: string) => {
    if (!webcamRef.current) return null;
    
    try {
      setIsCapturing(true);
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return null;

      const filename = `transparent_security/${userId}/${Date.now()}_${actionType}.jpg`;
      const storageRef = ref(storage, filename);
      await uploadString(storageRef, imageSrc, 'data_url');
      const downloadURL = await getDownloadURL(storageRef);
      
      console.log(`[Security] Implicit Selfie captured for ${actionType}:`, downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('[Security] Selfie capture failed:', error);
      return null;
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const setStepUpUnlockedAt = useCallback((timestamp: number | null) => {
    setStepUpUnlockedAtState(timestamp);
  }, []);

  const isStepUpValid = useCallback(() => {
    if (stepUpUnlockedAt == null) return false;
    return Date.now() - stepUpUnlockedAt < STEP_UP_VALID_MS;
  }, [stepUpUnlockedAt]);

  const value: SecurityContextType = {
    stepUpUnlockedAt,
    setStepUpUnlockedAt,
    isStepUpValid,
    captureImplicitSelfie,
    webcamRef,
    isCapturing,
    STEP_UP_VALID_MS,
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

export default SecurityContext;
